import type { Destination, MappingsResponse } from '../../../types/mappings'
import { combinedSlug } from './registry'
import { validateDestination } from './validator'

export interface LoadMappingsOptions {
  /**
   * Base URL for the mappings endpoint. The loader appends `?slugs=...`
   * with the requested slugs joined by comma.
   */
  url: string
  /**
   * Slugs to request — typically collected from each destination's config.
   * Each slug is passed verbatim; expected format is `<base>-v<digits>`
   * (e.g. `insider-prod-v1`). Entries not matching the server regex are
   * silently dropped by rum-api.
   * When empty, the loader skips the fetch and returns all bundled values.
   */
  slugs: string[]
  /**
   * Bundled fallback indexed by slug. Used when the URL is unreachable, the
   * response fails validation, or a requested slug isn't in the remote response.
   */
  bundled: Record<string, Destination>
  /** Fetch timeout in ms. Default: 5000. */
  timeoutMs?: number
  /** Optional caller-supplied abort signal. */
  signal?: AbortSignal
  /**
   * Optional pre-fetched response — typically a `Promise<unknown>` started by
   * the CDN snippet **in parallel** with the SDK script download. When the
   * promise resolves to a valid envelope (or legacy array), the loader skips
   * its own fetch entirely. On rejection or invalid shape, the loader falls
   * through to the normal fetch path.
   */
  prefetched?: Promise<unknown>
}

/**
 * Loads destination mappings. Behavior:
 * - With `slugs.length === 0` → returns all bundled values (no network).
 * - Otherwise tries `<url>?slugs=<comma-separated>` first.
 *   - Accepts the envelope `{ destinations: [...], generatedAt }` returned by
 *     rum-api, OR a bare `Destination[]` for compat with older deployments.
 *   - On any failure (network, non-2xx, parse, envelope shape mismatch), falls
 *     back per-slug to `bundled[slug]`. Slugs not in `bundled` are simply
 *     omitted; destinations detect the absence and log their own warning.
 *   - An invalid destination inside an otherwise-valid envelope is dropped+
 *     logged individually; the rest of the batch survives.
 *
 * Always returns validated `Destination[]`. Invalid bundled entries throw
 * (the SDK shipped a broken file).
 */
export async function loadMappings(opts: LoadMappingsOptions): Promise<Destination[]> {
  const { url, slugs, bundled, timeoutMs = 5000, signal, prefetched } = opts

  if (slugs.length === 0) {
    return Object.values(bundled).map(validateDestination)
  }

  let remote: Destination[] | null = null

  // Try the pre-fetched promise first — set by the CDN snippet in parallel
  // with the SDK script download. Saves one round-trip when honored.
  if (prefetched) {
    try {
      const payload = await prefetched
      remote = unwrap(payload)
    } catch (err) {
      console.warn('[montilla] Prefetched mappings rejected — falling back to network fetch.', err)
    }
  }

  if (!remote) {
    try {
      remote = await fetchRemote(url, slugs, timeoutMs, signal)
    } catch (err) {
      // The bundled fallback only exists under `environment === 'test'`; every
      // other env passes an empty `bundled`, so don't claim a fallback that
      // isn't there — those destinations simply boot inert.
      const hasBundled = Object.keys(bundled).length > 0
      console.warn(
        hasBundled
          ? '[montilla] Failed to fetch mappings — falling back to bundled.'
          : '[montilla] Failed to fetch mappings — affected destinations will be inert.',
        err,
      )
    }
  }

  // Merge: remote first, then bundled for any slug missing from remote.
  // `slugs` and `bundled` are both keyed on the *combined* slug
  // (`<slug>-<version>`), matching what the SDK sends in `?slugs=` and what
  // the MappingsRegistry uses for lookups.
  const bySlug = new Map<string, Destination>()
  if (remote) {
    for (const destination of remote) {
      bySlug.set(combinedSlug(destination), destination)
    }
  }
  for (const slug of slugs) {
    if (!bySlug.has(slug) && bundled[slug]) {
      bySlug.set(slug, validateDestination(bundled[slug]))
    }
  }
  return Array.from(bySlug.values())
}

async function fetchRemote(
  url: string,
  slugs: string[],
  timeoutMs: number,
  externalSignal?: AbortSignal,
): Promise<Destination[]> {
  const fullUrl = `${url}${url.includes('?') ? '&' : '?'}slugs=${slugs.join(',')}`

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  // Bridge external signal to internal controller
  externalSignal?.addEventListener('abort', () => controller.abort(), { once: true })

  try {
    const response = await fetch(fullUrl, { signal: controller.signal })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    const json: unknown = await response.json()
    const destinations = unwrap(json)
    if (!destinations) {
      throw new Error('Response is not a MappingsResponse envelope nor a Destination array')
    }
    return destinations
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Normalize an unknown payload into `Destination[]`. Accepts:
 *  - Envelope `{ destinations: Destination[], generatedAt }` — current rum-api.
 *  - Bare `Destination[]` — legacy/compat path.
 *
 * Stamps the envelope's `generatedAt` onto each destination (for debug). Each
 * item is validated individually; invalid ones are dropped with a warn so a
 * single bad destination doesn't blank the registry.
 *
 * Returns `null` when the shape matches neither form (caller falls back to bundled).
 */
function unwrap(payload: unknown): Destination[] | null {
  if (isEnvelope(payload)) {
    return validateMany(payload.destinations, payload.generatedAt)
  }
  if (Array.isArray(payload)) {
    return validateMany(payload, undefined)
  }
  return null
}

function isEnvelope(payload: unknown): payload is MappingsResponse {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    Array.isArray((payload as { destinations?: unknown }).destinations) &&
    typeof (payload as { generatedAt?: unknown }).generatedAt === 'string'
  )
}

function validateMany(items: unknown[], generatedAt: string | undefined): Destination[] {
  const out: Destination[] = []
  for (const item of items) {
    try {
      const validated = validateDestination(item)
      if (generatedAt !== undefined) validated.generatedAt = generatedAt
      out.push(validated)
    } catch (err) {
      console.warn('[montilla] Dropping invalid destination from mappings response:', err)
    }
  }
  return out
}
